import moment, { Moment } from 'moment-timezone';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import styles from './AddonsInsights.module.scss';
import { Period } from '@spike/model';
import Header from '../../components/Header';
import ToggleTabs from 'components/Insights/components/ToogleTabs/ToggleTabs';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useTimeZone } from 'hooks';
import { Pagination } from 'components/UI/V3/Pagination/Pagination';

import { getAddonsInsightsReport } from 'actions/insights/sales/addonsInsightsReport/AddonsInsightsReportActions';
import AddonsTable from './AddonsTable';
import AddonsInsightsCharts from './AddonsInsightsCharts';

interface AddonsInsightsProps {
    description: string;
}

const toDateParam = (m: Moment) => m.format('YYYY-MM-DD');

const options = [
    { id: 'table', label: 'Table' },
    { id: 'chart', label: 'Chart' }
];

const AddonsInsights: FunctionComponent<AddonsInsightsProps> = ({ description }) => {
    const dispatch = useDispatch();
    const timeZone = useTimeZone();

    const { report, loading } = useSelector((s: RootState) => s.addonsInsightsReport);

    const [currentTab, setCurrentTab] = useState('table');

    const defaultPeriod: Period = useMemo(() => {
        const now = moment().tz(timeZone);
        return { from: now.clone().startOf('month'), to: now.clone().endOf('month') };
    }, [timeZone]);

    const [period, setPeriod] = useState<Period | null>(defaultPeriod);
    const [allTime, setAllTime] = useState(false);

    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);

    const calendar = { optionSelected: 'This Month' };

    const fetchData = (p: Period | null, nextPage: number, nextSize: number) => {
        if (!p) {
            dispatch(getAddonsInsightsReport(undefined, undefined, nextPage, nextSize));
            return;
        }
        dispatch(getAddonsInsightsReport(toDateParam(p.from), toDateParam(p.to), nextPage, nextSize));
    };

    useEffect(() => {
        fetchData(period, page, size);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChangePeriod = (p: Period | null, isAllTime: boolean) => {
        setAllTime(isAllTime);
        setPage(1);

        if (isAllTime || !p) {
            setPeriod(null);
            fetchData(null, 1, size);
            return;
        }

        setPeriod({ from: p.from, to: p.to });
        fetchData({ from: p.from, to: p.to }, 1, size);
    };

    const handlePageChange = (nextPage: number) => {
        setPage(nextPage);
        fetchData(period, nextPage, size);
    };

    const handleSizeChange = (nextSize: number) => {
        setSize(nextSize);
        setPage(1);
        fetchData(period, 1, nextSize);
    };

    const exportFile = useMemo(() => {
        const rows = report?.addons ?? [];
        return {
            name: 'Addons Insights.csv',
            dataInMemory: true,
            headers: [
                'Add-On',
                'Category',
                'Quantity',
                'Avg Price',
                'Gross Sales',
                'Discounts',
                'Refunds',
                'Net Sales',
                '% of Net Add-On Sales',
                '% of All Net Sales'
            ],
            dataToExport: rows.map(r => [
                r.addonName ?? '—',
                r.category ?? '—',
                String(r.quantity ?? 0),
                Number(r.avgPrice ?? 0).toFixed(2),
                Number(r.grossSales ?? 0).toFixed(2),
                Number(r.discounts ?? 0).toFixed(2),
                Number(r.refunds ?? 0).toFixed(2),
                Number(r.netSales ?? 0).toFixed(2),
                ((Number(r.netAddonsSalesPct ?? 0) * 100).toFixed(2) + '%') as string,
                ((Number(r.allNetSalesPct ?? 0) * 100).toFixed(2) + '%') as string
            ]) as string[][]
        };
    }, [report]);

    return (
        <div className={styles.container}>
            <div className={styles.sectionInnerPadding}>
                <div>{description}</div>

                <div className={styles.categoryContainer}>
                    <ToggleTabs
                        options={options}
                        value={currentTab}
                        setValue={setCurrentTab}
                    />
                </div>

                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                    hideAllTime={true}
                />
            </div>

            {currentTab === 'table' && (
                <div className={styles.tableContainer}>
                    <AddonsTable
                        loading={loading}
                        report={report}
                    />
                </div>
            )}

            {currentTab === 'chart' && (
                <div className={styles.chartContainer}>
                    <AddonsInsightsCharts
                        loading={loading}
                        report={report}
                    />
                </div>
            )}

            {report && report.addons.length > 0 && (
                <div className={styles.paginationWrap}>
                    <Pagination
                        page={report?.meta?.page ?? page}
                        totalPages={report?.meta?.totalPages ?? 1}
                        size={report?.meta?.perPage ?? size}
                        isLoading={loading}
                        onPageChange={handlePageChange}
                        onSizeChange={handleSizeChange}
                    />
                </div>
            )}
            <div className={styles.spacer80} />
        </div>
    );
};

export default AddonsInsights;
